<script setup lang="ts">
import type { DefDialogState, DialogName } from '@/stores/useDialogStore';

import { computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAbility } from '@casl/vue';

import { extractImagePathForDeletion } from '@/utils/Common';
import TableAlert from '@/components/alerts/TableAlert.vue';
import TableFooterBar from '@/components/bars/TableFooterBar.vue';
import DetailedJobAppBarChipsBasicList from '@/components/basicLists/DetailedJobAppBarChipsBasicList.vue';
import KBtn from '@/components/btns/KBtn.vue';
import DetailedJobAdditionalInfoDescList from '@/components/descLists/DetailedJobAdditionalInfoDescList.vue';
import DetailedJobAttachmentsDescList from '@/components/descLists/DetailedJobAttachmentsDescList.vue';
import DetailedJobBaseInfoDescList from '@/components/descLists/DetailedJobBaseInfoDescList.vue';
import DetailedJobLastUpdatedDescList from '@/components/descLists/DetailedJobLastUpdatedDescList.vue';
import DetailedJobPhotosDescList from '@/components/descLists/DetailedJobPhotosDescList.vue';
import JobDiagramDescList from '@/components/descLists/JobDiagramDescList.vue';
import JobInstructionsDescList from '@/components/descLists/JobInstructionsDescList.vue';
import JobSignatureDescList from '@/components/descLists/JobSignatureDescList.vue';
import DetailedJobWideJobStopDiagram from '@/components/diagrams/DetailedJobWideJobStopDiagram.vue';
import DetailedJobAttachmentsForm from '@/components/forms/DetailedJobAttachmentsForm.vue';
import SimpleLoader from '@/components/loaders/SimpleLoader.vue';
import JobMap from '@/components/maps/JobMap.vue';
import DetailedJobAirRoadJobsTable from '@/components/tables/DetailedJobAirRoadJobsTable.vue';
import DetailedJobProductsTable from '@/components/tables/DetailedJobProductsTable.vue';
import DetailedJobQueriesTable from '@/components/tables/DetailedJobQueriesTable.vue';
import DetailedJobTicketsTable from '@/components/tables/DetailedJobTicketsTable.vue';
import ScheduleSwitchDetailedJobPageUnit from '@/components/units/ScheduleSwitchDetailedJobPageUnit.vue';
import DetailedJobFooterVMenu from '@/components/vMenus/DetailedJobFooterVMenu.vue';
import DetailedJobShowTechnicalDataVMenu from '@/components/vMenus/DetailedJobShowTechnicalDataVMenu.vue';
import DetailedJobBlockWrapper from '@/components/wrappers/DetailedJobBlockWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import useDetailedJobPageViewData from '@/composables/data/useDetailedJobPageViewData';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import { useCloneJobDialog } from '@/composables/useCloneJobDialog';
import { useDetailedJobPageViewStore } from '@/stores/useDetailedJobPageViewStore';
import { useDialogStore } from '@/stores/useDialogStore';
import { fetchDeleteJobStopImage } from '@/helpers/fetchers';
import * as routeQueryHelpers from '@/helpers/routeQuery';
import * as routeNames from '@/constants/routeNames';

const ability = useAbility();
const isStaff = computed(() => ability.can('staff', 'Job'));

const route = useRoute();
const router = useRouter();

const routeQuery = useCommonRouteQuery({
  queryJobId: routeQueryHelpers.getQueryNumberValue(route.params.job_id),
});

const detailedJobPageViewStore = useDetailedJobPageViewStore();
detailedJobPageViewStore.init(
  useDetailedJobPageViewData(routeQuery),
  routeQuery,
);

const dialogStore = useDialogStore();
const cloneJobDialog = useCloneJobDialog();

const hasAnyStopImages = computed(() => {
  if (!detailedJobPageViewStore.data.stopImages) return false;
  return Object.values(detailedJobPageViewStore.data.stopImages).some(
    (photos) => photos.length > 0,
  );
});

const isJobInvoiced = computed(
  () =>
    detailedJobPageViewStore.data.detailedJob?.invoice_status === 'Invoiced',
);

function handleNewQueryClick() {
  if (!detailedJobPageViewStore.data.detailedJob) return;

  const dialogName: DialogName = 'newQuery';
  dialogStore
    .open(dialogName, { job: detailedJobPageViewStore.data.detailedJob })
    .confirmed(async function () {
      if (detailedJobPageViewStore.data.refreshDetailedJob) {
        await detailedJobPageViewStore.data.refreshDetailedJob();
      }
    })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        // TODO close menu
        // if (closeDetail === 'confirm') emit('done', props.id);
      },
    );
}

function handleSplitToBaseClick() {
  if (!detailedJobPageViewStore.data.detailedJob) return;

  const dialogName: DialogName = 'splitToBase';
  dialogStore
    .open(dialogName, { job: detailedJobPageViewStore.data.detailedJob })
    .once(
      'closed',
      function (closeDetail: DefDialogState[typeof dialogName]['closeDetail']) {
        // TODO close menu
        // if (closeDetail === 'confirm') emit('done', props.id);
      },
    );
}

function handleCloneTheJobClick() {
  if (!detailedJobPageViewStore.data.detailedJob) return;
  cloneJobDialog.open(
    detailedJobPageViewStore.data.detailedJob.rockhopper_id,
    detailedJobPageViewStore.data.detailedJob.rockhopper_date,
  );
}

function openNewRFCDialog() {
  if (!detailedJobPageViewStore.data.detailedJob) return;

  const dialogName: DialogName = 'newRFC';
  dialogStore
    .open(dialogName, {
      jobId: detailedJobPageViewStore.data.detailedJob.rockhopper_id,
      jobDate: detailedJobPageViewStore.data.detailedJob.rockhopper_date,
    })
    .confirmed(async function () {
      if (detailedJobPageViewStore.data.refreshShortZammadTickets) {
        await detailedJobPageViewStore.data.refreshShortZammadTickets();
      }
    });
}

function openNewTicketDialog() {
  if (!detailedJobPageViewStore.data.detailedJob) return;
  if (!detailedJobPageViewStore.data.detailedCustomer) return;

  // TODO it seems ops_emails, channelsEmails should be writable
  const dialogName: DialogName = 'newTicket';
  dialogStore
    .open(dialogName, {
      jobId: detailedJobPageViewStore.data.detailedJob.rockhopper_id,
      jobDate: detailedJobPageViewStore.data.detailedJob.rockhopper_date,
      // @ts-expect-error
      opsEmails: detailedJobPageViewStore.data.detailedCustomer.ops_emails,
      // @ts-expect-error
      channelsEmails: detailedJobPageViewStore.data.channelsEmails,
    })
    .confirmed(async function () {
      if (detailedJobPageViewStore.data.refreshShortZammadTickets) {
        await detailedJobPageViewStore.data.refreshShortZammadTickets();
      }
    });
}

async function deleteImage(stopNum: string | number, photoIndex: number) {
  if (!detailedJobPageViewStore.data.detailedJob) return;
  if (!detailedJobPageViewStore.data.stopImages) return;

  const stopKey = String(stopNum);
  const photos = detailedJobPageViewStore.data.stopImages[stopKey];
  if (!photos[photoIndex]) return;

  const photo = photos[photoIndex];
  const imagePath = extractImagePathForDeletion(photo.path);

  await fetchDeleteJobStopImage({
    job_id: detailedJobPageViewStore.data.detailedJob.rockhopper_id,
    job_date: detailedJobPageViewStore.data.detailedJob.rockhopper_date,
    image_path: imagePath,
  });

  if (detailedJobPageViewStore.data.refreshStopImages) {
    await detailedJobPageViewStore.data.refreshStopImages();
  }
}

function handleEditClick(): void {
  if (!detailedJobPageViewStore.data.detailedJob) return;

  void router.push({
    name: routeNames.JOB_EDIT,
    query: {
      job_id: detailedJobPageViewStore.data.detailedJob.rockhopper_id,
      job_date: detailedJobPageViewStore.data.detailedJob.rockhopper_date,
    },
  });
}

onMounted(() => {
  // maybe need more common solution for all pages and taking back/forward behavior into account
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
});

onUnmounted(function () {
  detailedJobPageViewStore.$reset();
});
</script>

<template>
  <div :class="$style.detailedJobPageView" data-testid="detailedJobPageView">
    <Teleport to="#app-bar-chips">
      <DetailedJobAppBarChipsBasicList
        :job="detailedJobPageViewStore.data.detailedJob"
        :attachments="detailedJobPageViewStore.updatedAttachments"
      />
    </Teleport>
    <template
      v-if="
        !(
          detailedJobPageViewStore.data.detailedJob &&
          detailedJobPageViewStore.data.detailedCustomer
        ) && detailedJobPageViewStore.isFetchingAllData
      "
    >
      <SimpleLoader :size="64" :width="6" class="mx-auto my-20" />
    </template>
    <template v-else>
      <div :class="$style.secondHeader">
        <div :class="$style.widthWrapper">
          <div :class="$style.secondHeaderLeftItem">
            <ScheduleSwitchDetailedJobPageUnit
              v-if="isStaff"
              :job="detailedJobPageViewStore.data.detailedJob"
              :refresh-detailed-job="
                detailedJobPageViewStore.data.refreshDetailedJob
              "
            />
          </div>
          <div :class="$style.secondHeaderRightItem">
            <div class="mr-3">
              <DetailedJobShowTechnicalDataVMenu
                :job="detailedJobPageViewStore.data.detailedJob"
              />
            </div>
            <DetailedJobLastUpdatedDescList
              :updated-at="
                detailedJobPageViewStore.data.detailedJob?.last_activity_at
              "
            />
          </div>
        </div>
      </div>
      <PageContentWrapper>
        <div :class="$style.widthWrapper">
          <div
            v-if="detailedJobPageViewStore.data.detailedJob?.warnings?.length"
            :class="$style.warnings"
          >
            <template
              v-for="(warningItem, warningIndex) in detailedJobPageViewStore
                .data.detailedJob.warnings"
              :key="warningIndex"
            >
              <TableAlert v-if="warningItem.message" :class="$style.warning">
                {{ warningItem.message }}
              </TableAlert>
            </template>
          </div>
          <div :class="$style.mapAndInfo">
            <DetailedJobBlockWrapper :class="$style.mapAndBaseInfo">
              <!-- TODO should be fixed after openapi update -->
              <!-- @vue-ignore -->
              <DetailedJobBaseInfoDescList
                :job="detailedJobPageViewStore.data.detailedJob"
                :detailed-customer="
                  detailedJobPageViewStore.data.detailedCustomer
                "
                :is-staff="isStaff"
              />
              <JobMap
                :stop-locations="detailedJobPageViewStore.data.stopLocations"
                class="mt-6"
              />
            </DetailedJobBlockWrapper>
            <div :class="$style.additionalInfo">
              <h3 :class="$style.h3">Additional information</h3>
              <!-- TODO should be fixed after openapi update -->
              <!-- @vue-ignore -->
              <DetailedJobAdditionalInfoDescList
                :job="detailedJobPageViewStore.data.detailedJob"
                :detailed-customer="
                  detailedJobPageViewStore.data.detailedCustomer
                "
                :loading="detailedJobPageViewStore.isFetchingAllData"
                :is-staff="isStaff"
                :class="$style.additionalInfoList"
              />
            </div>
          </div>
          <DetailedJobBlockWrapper :class="$style.diagramAndLists">
            <DetailedJobWideJobStopDiagram
              :class="$style.stopDiagram"
              class="mb-6 pt-6"
              :job="detailedJobPageViewStore.data.detailedJob"
              :refresh-detailed-job="
                detailedJobPageViewStore.data.refreshDetailedJob
              "
            />
            <div
              v-if="detailedJobPageViewStore.data.detailedJob?.stops"
              :class="$style.diagramLists"
              class="mt-n1"
            >
              <div
                v-for="(stop, stopIndex) in detailedJobPageViewStore.data
                  .detailedJob.stops"
                :key="stopIndex"
                :class="$style.diagramList"
              >
                <!-- TODO should be fixed after openapi update -->
                <!-- @vue-ignore -->
                <JobDiagramDescList :stop="stop" />
                <JobInstructionsDescList class="mt-4" :text="stop.note_text" />
                <JobSignatureDescList
                  v-if="stop.is_pod_image || stop.pod_time"
                  class="mt-4"
                  :job-id="
                    detailedJobPageViewStore.data.detailedJob.rockhopper_id
                  "
                  :job-date="
                    detailedJobPageViewStore.data.detailedJob.rockhopper_date
                  "
                  :stop-number="stop.stop_number"
                  :pod-name="stop.pod_name"
                  :is-pod-image="stop.is_pod_image"
                />
              </div>
            </div>
          </DetailedJobBlockWrapper>

          <template
            v-for="(
              detailedJobProductsTableData, stopNumberKey
            ) in detailedJobPageViewStore.productsTablesData"
            :key="stopNumberKey"
          >
            <DetailedJobBlockWrapper :class="$style.productsBlockWrapper">
              <h3 :class="$style.h3">
                Stop {{ Number(stopNumberKey) }} to Stop
                {{ Number(stopNumberKey) + 1 }}
                products
              </h3>
              <div class="mx-n6 mb-n6">
                <DetailedJobProductsTable
                  :data="detailedJobProductsTableData"
                  :loading="detailedJobPageViewStore.isFetchingAllData"
                  :is-staff="isStaff"
                />
                <div :class="$style.productsTableFooter">
                  <div>
                    <KBtn
                      variant="text"
                      :class="[
                        {
                          'invisible':
                            detailedJobPageViewStore.productsTableShowMoreLimit >=
                            detailedJobPageViewStore.tablesDataLengths[
                              stopNumberKey
                            ],
                        },
                        $style.productsTableBtn,
                      ]"
                      @click="
                        detailedJobPageViewStore.handleProductsTableToggleMoreClick(
                          stopNumberKey,
                        )
                      "
                    >
                      Show
                      {{
                        detailedJobPageViewStore.productsTableShowMore[
                          stopNumberKey
                        ]
                          ? 'less'
                          : 'more'
                      }}
                    </KBtn>
                  </div>
                  <div
                    >Total:
                    {{
                      detailedJobPageViewStore.tablesDataLengths[stopNumberKey]
                    }}</div
                  >
                </div>
              </div>
            </DetailedJobBlockWrapper>
          </template>

          <div :class="$style.ticketsAndQueriesTables">
            <div>
              <DetailedJobBlockWrapper :class="$style.ticketsBlockWrapper">
                <h3 :class="$style.h3">Tickets</h3>
                <menu :class="$style.tableMenuList">
                  <li :class="$style.tableMenuListItem">
                    <KBtn
                      variant="text"
                      append-icon="mdi-plus"
                      size="lg"
                      :class="$style.productsTableBtn"
                      :disabled="!openNewTicketDialog"
                      @click="openNewTicketDialog"
                    >
                      New ticket
                    </KBtn>
                  </li>
                  <li :class="$style.tableMenuListItem">
                    <KBtn
                      variant="text"
                      append-icon="mdi-plus"
                      size="lg"
                      :class="$style.productsTableBtn"
                      :disabled="!openNewRFCDialog"
                      @click="openNewRFCDialog"
                    >
                      New request for change
                    </KBtn>
                  </li>
                </menu>
                <div class="mx-n6 mb-n6">
                  <DetailedJobTicketsTable
                    :data="detailedJobPageViewStore.detailedJobTicketsTableData"
                    :loading="detailedJobPageViewStore.isFetchingAllData"
                  />
                </div>
              </DetailedJobBlockWrapper>
            </div>
            <div>
              <DetailedJobBlockWrapper :class="$style.queriesBlockWrapper">
                <h3 :class="$style.h3">Queries</h3>
                <TableAlert v-if="isJobInvoiced" :class="$style.invoicedAlert">
                  Job is invoiced. Please use "Mail" section if you need to add
                  information.
                </TableAlert>
                <menu v-else :class="$style.tableMenuList">
                  <li :class="$style.tableMenuListItem">
                    <KBtn
                      variant="text"
                      append-icon="mdi-plus"
                      size="lg"
                      :class="$style.productsTableBtn"
                      @click="handleNewQueryClick"
                    >
                      New query
                    </KBtn>
                  </li>
                </menu>
                <div class="mx-n6 mb-n6">
                  <DetailedJobQueriesTable
                    :data="detailedJobPageViewStore.detailedJobQueriesTableData"
                    :loading="detailedJobPageViewStore.isFetchingAllData"
                    :is-staff="isStaff"
                  />
                </div>
              </DetailedJobBlockWrapper>
            </div>
          </div>

          <DetailedJobBlockWrapper :class="$style.airRoadJobsBlockWrapper">
            <h3 :class="$style.h3">AirRoad jobs</h3>
            <div class="mx-n6 mb-n6">
              <DetailedJobAirRoadJobsTable
                :data="detailedJobPageViewStore.detailedJobAirRoadJobsTableData"
                :loading="detailedJobPageViewStore.isFetchingAllData"
              />
            </div>
          </DetailedJobBlockWrapper>

          <div :class="$style.attachmentsAndPhotos">
            <div>
              <DetailedJobBlockWrapper
                id="attachments"
                :class="$style.attachments"
              >
                <h3 :class="$style.h3">Attachments</h3>
                <DetailedJobAttachmentsDescList
                  v-if="detailedJobPageViewStore.updatedAttachments?.length"
                  :attachments="detailedJobPageViewStore.updatedAttachments"
                  :refresh-detailed-job="
                    detailedJobPageViewStore.data.refreshDetailedJob
                  "
                />
                <DetailedJobAttachmentsForm
                  :initial-data="detailedJobPageViewStore.initialData"
                  :loading="detailedJobPageViewStore.isFetchingFormData"
                  @submit="detailedJobPageViewStore.handleSubmit"
                />
              </DetailedJobBlockWrapper>
            </div>
            <div>
              <DetailedJobBlockWrapper
                v-if="
                  detailedJobPageViewStore.data.detailedJob?.has_stop_images &&
                  detailedJobPageViewStore.data.stopImages &&
                  hasAnyStopImages
                "
                :class="$style.photos"
              >
                <h3 :class="$style.h3">Photos</h3>
                <div
                  v-for="(photos, stopNum) in detailedJobPageViewStore.data
                    .stopImages"
                  :key="stopNum"
                >
                  <template v-if="photos.length">
                    <h4>Stop {{ stopNum }}</h4>
                    <DetailedJobPhotosDescList
                      :photos="photos"
                      :delete-image="
                        (photoIndex) => deleteImage(stopNum, photoIndex)
                      "
                    />
                  </template>
                </div>
              </DetailedJobBlockWrapper>
            </div>
          </div>
        </div>
      </PageContentWrapper>
      <TableFooterBar>
        <DetailedJobFooterVMenu
          :job="detailedJobPageViewStore.data.detailedJob"
          :open-new-ticket-dialog="openNewTicketDialog"
          :open-new-query-dialog="handleNewQueryClick"
          :open-split-to-base-dialog="handleSplitToBaseClick"
          :refresh-detailed-job="
            detailedJobPageViewStore.data.refreshDetailedJob
          "
        />
        <KBtn
          class="mx-4"
          size="lg"
          variant="outlined"
          append-icon="mdi-arrow-down"
          :loading="detailedJobPageViewStore.isDownloading"
          @click="detailedJobPageViewStore.handleDownloadShippingLabelClick"
        >
          <span class="tracking-tight">Download Shipping Label</span>
        </KBtn>
        <KBtn
          class="mr-4"
          size="lg"
          variant="outlined"
          append-icon="mdi-content-duplicate"
          @click="handleCloneTheJobClick"
        >
          Clone the job
        </KBtn>
        <KBtn
          v-if="isStaff"
          class="mr-4"
          size="lg"
          variant="outlined"
          append-icon="mdi-clipboard-clock-outline"
          :to="{
            name: routeNames.JOB_HISTORY,
            query: {
              job_id: detailedJobPageViewStore.data.detailedJob?.rockhopper_id,
              job_date:
                detailedJobPageViewStore.data.detailedJob?.rockhopper_date,
            },
          }"
        >
          Job history
        </KBtn>
        <KBtn
          v-if="isStaff"
          class="mr-4"
          size="lg"
          variant="outlined"
          append-icon="mdi-pencil-outline"
          @click="handleEditClick"
        >
          Edit
        </KBtn>
      </TableFooterBar>
    </template>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.detailedJobPageView {
}
  .widthWrapper {
    max-width: variables.$tableWrapperWidth;
  }

  h3.h3 {
    font-weight: 500;
    font-size: 18px;
    padding: 0;
    margin: 0 0 24px 0;
  }

  .secondHeader {
    /* background-color: colors.$color-brand-50; */
    background-color: colors.$color-white;
    border-bottom: 1px solid colors.$color-brand-100;
    min-height: 64px;
    display: flex;
    align-items: center;

    .widthWrapper {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 0 variables.$pageMarginX;
    }
      .secondHeaderLeftItem {
        display: flex;
      }
      .secondHeaderRightItem {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
      }
  }
  .secondHeaderBelow {
    margin: 0 variables.$pageMarginX;
    max-width: variables.$tableWrapperWidth;
    margin-top: 12px;
    margin-bottom: -4px;
  }
    .warnings {
      margin-top: -1rem;
      margin-bottom: 1rem;
    }
      .warning {
      }
    .mapAndInfo {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
      gap: 2rem;
      margin-bottom: 1rem;

      @media (max-width: 1024px) {
        grid-template-columns: 1fr;
      }
    }
      .rightColumn {
        display: flex;
        flex-direction: column;
      }
      .mapWrapper {
        margin-top: 2rem;
      }
      .additionalInfo {
        line-height: 1.4;
        .additionalInfoList {
          margin-left: 12px;
        }
      }
    .diagramAndLists {
      margin: 2rem 0;
      padding: 0;
    }
      .stopDiagram {
      }
      .diagramLists {
        display: flex;
      }
        .diagramList {
          width: 100%;
          padding: 0rem 1.5rem 1.5rem 3rem;
        }
        .diagramList + .diagramList {
          border-left: 1px solid colors.$color-brand-100;
        }
    .productsBlockWrapper {
      margin: 2rem 0;
    }
      .productsTableFooter {
        padding: 12px 0;
        display: flex;
        align-items: center;
        justify-content: space-between;

        & > div:first-child {
          padding-left: 6px;
        }

        & > div:last-child {
          padding-right: 22px;
        }
      }
        .productsTableBtn {
          text-transform: none;
        }
    .ticketsAndQueriesTables {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(580px, 1fr));
      gap: 2rem;
      margin: 2rem 0;
    }
      .ticketsBlockWrapper {
        overflow: hidden;
        position: relative;
      }
      .queriesBlockWrapper {
        position: relative;
      }
        .invoicedAlert {
          margin-bottom: 0.5rem;
        }
        .tableMenuList {
          list-style: none;
          padding: 0;
          margin: 0;
          position: absolute;
          top: 14px;
          right: 7px;
        }
          .tableMenuListItem {
            display: inline;
          }
    .airRoadJobsBlockWrapper {
      margin: 2rem 0;
    }
    .attachmentsAndPhotos {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
      gap: 2rem;
    }
      .attachments {
      }
      .photos {
        display: inline-block;
      }
</style>
